import { useState } from 'react';
import { downloadFromApi } from '@utils/download';
import { convertToRailsQueryString } from '@utils/api';
import { GetCampaignParams } from '@features/campaigns';
import {
  BaseDownloadModal,
  BaseDownloadModalProps,
} from '@features/reports/components/BaseDownloadModal';
import { RslExecutiveReportDownload } from '@components/shared/Reports/RslExecutiveReportDownload';
import { RslLciReportDownloadContainer } from '@components/shared/Reports/RslLciReportDownloadButton';
import { RslReportsParams } from '@features/campaigns/subdomains/rsl';

type DownloadModalProps = {
  campaignName: string;
  campaignId: number;
  reportId: number;
  renderOpener: BaseDownloadModalProps['renderOpener'];
  campaignParams: GetCampaignParams;
  channel: string | null;
  hasVisitMetrics: boolean;
};

export const DownloadModal = (props: DownloadModalProps) => {
  const {
    campaignName,
    campaignId,
    reportId,
    renderOpener,
    campaignParams,
    channel,
    hasVisitMetrics,
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const reportParams: Omit<RslReportsParams, 'campaignId'> = {
    includeUnapproved: campaignParams.include_unapproved ?? false,
    showRestrictedValues: false,
    channel,
  };

  const downloadFlatReport = () => {
    downloadFromApi(
      `/api/v3/rsl/campaigns/${campaignId}/reports/${reportId}/flat_file?${convertToRailsQueryString(
        reportParams
      )}`
    );
  };

  const downloadSalesSummaryReport = () => {
    downloadFromApi(
      `/api/v3/rsl/campaigns/${campaignId}/reports/${reportId}/sales_summary?${convertToRailsQueryString(
        reportParams
      )}`
    );
  };

  const downloadSummaryReport = () => {
    downloadFromApi(
      `/api/v3/rsl/campaigns/${campaignId}/reports/${reportId}/summary_file?${convertToRailsQueryString(
        reportParams
      )}`
    );
  };

  const downloadPancakeReport = () => {
    downloadFromApi(
      `/api/v3/rsl/campaigns/${campaignId}/reports/${reportId}/pancake?${convertToRailsQueryString(
        reportParams
      )}`
    );
  };

  return (
    <BaseDownloadModal
      campaignId={campaignId}
      campaignName={campaignName}
      renderOpener={renderOpener}
      isOpen={isOpen}
      onOpenerClick={() => setIsOpen(true)}
      onCloserClick={() => setIsOpen(false)}
      downloadOptions={[
        ...(hasVisitMetrics
          ? [
              {
                name: 'summary',
                group: 'Data Output',
                label: 'Summary (.xlsx)',
                onClick: downloadSummaryReport,
              },
            ]
          : []),
        {
          name: 'flat',
          group: 'Data Output',
          label: 'Standard (.csv)',
          onClick: downloadFlatReport,
        },
        {
          name: 'salesSummary',
          group: 'Data Output',
          label: 'Table (.xlsx)',
          onClick: downloadSalesSummaryReport,
        },
        ...(hasVisitMetrics
          ? [
              {
                name: 'pancake',
                group: 'Data Output',
                label: 'Pancake (.csv)',
                internal: true,
                onClick: downloadPancakeReport,
              },
            ]
          : []),
        {
          name: 'lciReport',
          group: 'Presentation',
          component: (
            <RslLciReportDownloadContainer
              campaignId={campaignId}
              campaignParams={campaignParams}
              reportParams={reportParams}
            />
          ),
        },
        {
          name: 'executiveReport',
          group: 'Presentation',
          component: (
            <RslExecutiveReportDownload
              campaignId={campaignId}
              reportParams={reportParams}
              campaignParams={campaignParams}
            />
          ),
        },
      ]}
    />
  );
};
